import React, { Dispatch, SetStateAction, useMemo } from 'react';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import { IconDefaultsContextProvider } from '@resources/js/contexts/useIconDefaults';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import Table, {
  AscendingSortIcon,
  TableColumn,
  TableProps,
} from '@resources/js/components/Table/Table';
import HorizontalContent from '@resources/js/components/HorizontalContent/HorizontalContent';
import { Cell, ColumnInstance } from 'react-table';
import Button from '@resources/js/components/Button/Button';
import Bar from '@resources/js/components/Bar/Bar';
import Bars from '@resources/js/components/Bars/Bars';
import { BuildsThroughputSectionViewModel } from '@resources/js/components/BuildsThroughputSection/BuildsThroughputSection';
import useThroughputBreakdownHiddenColumns from '@resources/js/contexts/useThroughputBreakdownHiddenColumns';

export type ThroughputBreakdownViewModel = {
  title: string;
  description: string;
  table: {
    columns: Record<ColumnKey, TableColumn | null | undefined>;
    data: Row[];
    footer: Row;
  };
  showAllColumnsLabel: string;
};

export default function ThroughputBreakdown({
  title,
  description,
  table,
  showAllColumnsLabel,
}: ThroughputBreakdownViewModel): JSX.Element {
  const { showAllColumns, setShowAllColumns } =
    useThroughputBreakdownHiddenColumns();

  return (
    <VerticalContent>
      <EnhancedMarkdown
        markdown={`
## ${title}

${description ?? ''}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      <IconDefaultsContextProvider
        overrides={{
          ability: { bordered: true },
          icon: { bordered: true, rounded: false },
        }}
      >
        <BreakdownTable
          table={table}
          limit={10}
          showAllColumns={showAllColumns}
          setShowAllColumns={setShowAllColumns}
          showAllColumnsLabel={showAllColumnsLabel}
        />
      </IconDefaultsContextProvider>
    </VerticalContent>
  );
}

type ColumnKey =
  | 'name'
  | 'amountPercent'
  | 'amountBar'
  | 'amountTotal'
  | 'absorbed'
  | 'casts'
  | 'castsPerMinute'
  | 'averageCast'
  | 'hits'
  | 'averageHit'
  | 'critPercent'
  | 'uptimePercent'
  | 'missPercent'
  | 'overhealPercent'
  | 'mitigatedPercent'
  | 'amountPerSecond';

type Row = {
  name: string;
  amountPercent: number;
  amountBar: number;
  barOnePercent: number;
  barTwoPercent: number;
  amountTotal: number;
  absorbed: number;
  casts: number;
  castsPerMinute: number;
  averageCast: number;
  hits: number;
  averageHit: number;
  critPercent: number;
  uptimePercent: number;
  missPercent: number;
  overhealPercent: number;
  mitigatedPercent: number;
  amountPerSecond: number;
  abilityType: number;
  averageHitCast: number;
  averageTickCast: number;
  tickHits: number;
  averageHitHit: number;
  averageTickHit: number;
  tickCritPercent: number;
  tickMissPercent: number;
};

function BreakdownTable({
  table,
  limit,
  showAllColumns,
  setShowAllColumns,
  showAllColumnsLabel,
}: {
  table: BuildsThroughputSectionViewModel['breakdowns'][0]['table'];
  limit: number;
  showAllColumns: boolean;
  setShowAllColumns: Dispatch<SetStateAction<boolean>>;
  showAllColumnsLabel: string;
}): JSX.Element {
  const columns = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(table?.columns ?? {}).map(([accessor, column]) => [
          accessor,
          {
            ...column,
            renderCell,
            renderFooter,
          } as TableColumn<Row>,
        ])
      ),
    [table.columns]
  );

  const truncate: TableProps['truncate'] = {
    showMoreButton: { styles: 'inline-row' },
  };

  if (limit) {
    truncate.limit = limit;
  }

  return (
    <VerticalContent gap={'smaller'}>
      <Table
        style={'report'}
        {...table}
        columns={columns}
        truncate={truncate}
      />
      <div style={{ display: 'flex' }}>
        <ShowAllColumnsToggle
          showAllColumns={showAllColumns}
          setShowAllColumns={setShowAllColumns}
          showAllColumnsLabel={showAllColumnsLabel}
        />
      </div>
    </VerticalContent>
  );
}

function ShowAllColumnsToggle({
  showAllColumns,
  setShowAllColumns,
  showAllColumnsLabel,
}: {
  showAllColumns: boolean;
  setShowAllColumns: Dispatch<SetStateAction<boolean>>;
  showAllColumnsLabel: string;
}): JSX.Element {
  return (
    <label>
      <HorizontalContent gap={'tiny'}>
        <input
          type={'checkbox'}
          checked={showAllColumns}
          onChange={() => setShowAllColumns((prev) => !prev)}
        />
        {showAllColumnsLabel}
      </HorizontalContent>
    </label>
  );
}

const formatValue = (value: number | '-') => {
  if (value === '-' || (Number.isInteger(value) && Math.abs(value) < 10000)) {
    return value;
  }

  if (Math.abs(value) > 1_000_000) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
};

const dashIfZero = (value: number) => (value === 0 ? '-' : value);

const maybeWithParenthesis = (
  value: string | number,
  secondaryValue: string | number
) => {
  if (secondaryValue && secondaryValue !== '-') {
    if (value && value !== '-') {
      return `${value} (${secondaryValue})`;
    } else {
      return secondaryValue;
    }
  }

  return value;
};

const formatPercent = (value: number | '-', fractionDigits: number = 2) => {
  return value === '-' ? value : value.toFixed(fractionDigits) + '%';
};

const renderCell = (cell: Cell<Row>) =>
  renderTableValue(
    cell.row.original,
    cell.column.id as ColumnKey,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    cell.row.canExpand,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    cell.row.isExpanded,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    cell.row.toggleRowExpanded,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    cell.row.depth
  );

const renderFooter = (column: ColumnInstance<Row>, footer?: Row) =>
  footer
    ? renderTableValue(
        footer,
        column.id as ColumnKey,
        false,
        false,
        () => {},
        0,
        true
      )
    : null;

const renderTableValue = (
  row: Row,
  accessor: ColumnKey,
  canExpand: boolean,
  isExpanded: boolean,
  toggleRowExpanded: (isExpanded: boolean) => void,
  expansionDepth: number,
  isFooter: boolean = false
) => {
  if (row[accessor] === null) {
    return null;
  }

  switch (accessor) {
    case 'name': {
      let child;

      if (canExpand) {
        child = (
          <HorizontalContent style={{ justifyContent: 'space-between' }}>
            <EnhancedMarkdown markdown={row.name} />
            <Button
              styles={['unstyled']}
              onClick={() => toggleRowExpanded(!isExpanded)}
            >
              <AscendingSortIcon style={'report'} />
            </Button>
          </HorizontalContent>
        );
      } else {
        child = <EnhancedMarkdown markdown={row.name} />;
      }

      return <div style={{ paddingLeft: expansionDepth * 26 }}>{child}</div>;
    }
    case 'amountPercent':
      return formatPercent(row.amountPercent);
    case 'amountBar': {
      const type = row.abilityType.toString();

      if (!row.barTwoPercent) {
        return <Bar type={type} percent={row.amountBar} />;
      }

      return (
        <Bars
          bars={[
            {
              type,
              percent: row.barOnePercent,
            },
            {
              type,
              percent: row.barTwoPercent,
              darken: true,
            },
          ]}
        />
      );
    }
    case 'amountTotal':
      return formatValue(row.amountTotal);
    case 'absorbed':
      return formatValue(row.absorbed);
    case 'casts':
      return formatValue(dashIfZero(row.casts));
    case 'castsPerMinute':
      return formatValue(dashIfZero(row.castsPerMinute));
    case 'averageCast':
      return maybeWithParenthesis(
        formatValue(dashIfZero(row.averageHitCast)),
        formatValue(dashIfZero(row.averageTickCast))
      );
    case 'hits':
      return maybeWithParenthesis(
        formatValue(dashIfZero(row.hits)),
        formatValue(dashIfZero(row.tickHits))
      );
    case 'averageHit':
      return maybeWithParenthesis(
        formatValue(dashIfZero(row.averageHitHit)),
        formatValue(dashIfZero(row.averageTickHit))
      );
    case 'critPercent':
      return maybeWithParenthesis(
        formatPercent(dashIfZero(row.critPercent), 1),
        formatPercent(dashIfZero(row.tickCritPercent), 1)
      );
    case 'uptimePercent':
      return formatPercent(dashIfZero(row.uptimePercent));
    case 'missPercent':
      return maybeWithParenthesis(
        formatPercent(dashIfZero(row.missPercent)),
        formatPercent(dashIfZero(row.tickMissPercent))
      );
    case 'overhealPercent':
      return formatPercent(dashIfZero(row.overhealPercent));
    case 'mitigatedPercent':
      return formatPercent(dashIfZero(row.mitigatedPercent));
    case 'amountPerSecond':
      return isFooter ? (
        formatValue(row.amountPerSecond)
      ) : (
        <span className={'primary'}>{formatValue(row.amountPerSecond)}</span>
      );
    default:
      return row[accessor];
  }
};
